import { useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { getTracks } from "@/modules/property-tracks/api";
import { Track } from "@/modules/property-tracks/types";

export interface LeadPropertyTrackOption {
  trackUId: string;
  unitNumber: string;
  compoundName: string;
  label: string;
}

const buildOption = (track: Track): LeadPropertyTrackOption => {
  const unitNumber = track.property?.unitId ?? "";
  const compoundName = track.property?.compound?.name ?? "";
  const shortTrackUid = track.uid ? track.uid.slice(0, 8) : "";

  const labelParts = [compoundName, unitNumber].filter(Boolean);
  const baseLabel = labelParts.join(" - ");
  const label = shortTrackUid
    ? `${baseLabel}${baseLabel ? " " : ""}(${shortTrackUid})`
    : baseLabel;

  return {
    trackUId: track.uid,
    unitNumber,
    compoundName,
    label,
  };
};

const useGetLeadPropertyTracks = ({
  enabled = true,
  pageSize = 50,
  journeyUId: journeyUIdProp,
}: {
  enabled?: boolean;
  pageSize?: number;
  journeyUId?: string;
} = {}) => {
  const { lead } = useContext(LeadPageContext);

  const journeyUId = journeyUIdProp ?? lead?.data?.lead?.journey_uid;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["getLeadPropertyTracks", journeyUId, pageSize],
    queryFn: () =>
      getTracks({
        filters: {
          journeyUId: journeyUId!,
        },
        pagination: {
          page: 1,
          pageSize,
          orderBy: "createdAt",
          orderDirection: "DESC",
        },
      }),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!journeyUId && enabled,
  });

  const tracks: Track[] = useMemo(
    () => data?.data?.results ?? [],
    [data?.data?.results],
  );

  const options: LeadPropertyTrackOption[] = useMemo(
    () => tracks.map(buildOption),
    [tracks],
  );

  return { tracks, options, isLoading, isError };
};

export default useGetLeadPropertyTracks;
